import React from "react";
import { motion } from "framer-motion";
import AnimatedCounter from "./AnimatedCounter";

const fadeUp = (delay = 0) => ({
  initial: { y: 24, opacity: 0 },
  animate: { y: 0, opacity: 1 },
  transition: { duration: 0.8, ease: [0.2, 0.7, 0.2, 1], delay },
});

const scores = [
  { label: "Health", value: 82, hue: "teal", from: "#2DD4BF", to: "#14B8A6" },
  { label: "Food", value: 74, hue: "emerald", from: "#34D399", to: "#10B981" },
  { label: "Mood", value: 68, hue: "lav", from: "#C4B5FD", to: "#A78BFA" },
  { label: "Sleep", value: 63, hue: "coral", from: "#FDA4AF", to: "#FB7185" },
];

function Dial({ value, from, to, label }) {
  const r = 42;
  const c = 2 * Math.PI * r;
  const off = c - (value / 100) * c;
  const gid = `g-${label}`;
  return (
    <div className="relative flex flex-col items-center justify-center">
      <svg width="112" height="112" viewBox="0 0 112 112" className="-rotate-90">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={from} /><stop offset="100%" stopColor={to} />
          </linearGradient>
        </defs>
        <circle cx="56" cy="56" r={r} stroke="rgba(255,255,255,0.07)" strokeWidth="6" fill="none" />
        <motion.circle cx="56" cy="56" r={r} stroke={`url(#${gid})`} strokeWidth="6" strokeLinecap="round" fill="none"
          strokeDasharray={c} initial={{ strokeDashoffset: c }} whileInView={{ strokeDashoffset: off }}
          viewport={{ once: true }} transition={{ duration: 1.6, ease: "easeOut", delay: 0.4 }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-heading text-2xl tracking-tighter"><AnimatedCounter value={value} /></div>
        <div className="text-[9px] uppercase tracking-[0.22em] text-zinc-500">{label}</div>
      </div>
    </div>
  );
}

function BiomarkerTrend() {
  return (
    <svg viewBox="0 0 300 70" className="w-full h-16">
      <defs>
        <linearGradient id="bm-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" />
        </linearGradient>
      </defs>
      <motion.path d="M0,50 L30,45 L60,48 L90,38 L120,42 L150,30 L180,32 L210,22 L240,26 L270,16 L300,12 L300,70 L0,70 Z"
        fill="url(#bm-fill)" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1.2 }} />
      <motion.polyline points="0,50 30,45 60,48 90,38 120,42 150,30 180,32 210,22 240,26 270,16 300,12"
        stroke="#2DD4BF" strokeWidth="2" fill="none"
        initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} viewport={{ once: true }} transition={{ duration: 1.6 }} />
      {[45, 30, 22, 16, 12].map((y, i) => (
        <motion.circle key={i} cx={30 + i * 60} cy={y} r="2.5" fill="#2DD4BF"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.4 + i * 0.15 }} />
      ))}
    </svg>
  );
}

export default function Hero() {
  return (
    <section id="top" data-testid="hero-section" className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-ink-900/40 via-ink-900/70 to-ink-900" />
        <div className="absolute inset-0 bg-grid opacity-[0.3]" />
        <div className="absolute -top-20 left-1/4 h-[560px] w-[560px] -translate-x-1/2 bg-radial-teal" />
        <div className="absolute top-40 right-0 h-[420px] w-[420px] translate-x-1/3 bg-radial-lav" />
        <div className="absolute bottom-0 left-1/3 h-[300px] w-[300px] bg-radial-coral opacity-40" />
        <div className="absolute inset-0 grain" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.05fr,0.95fr] gap-12 lg:gap-16 items-center">
          <div>
            <motion.div {...fadeUp(0)} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-teal-soft">
              <span className="h-1.5 w-1.5 rounded-full bg-teal-soft animate-pulse-glow" />
              AI Health Intelligence · Private Beta
            </motion.div>

            <motion.h1 {...fadeUp(0.05)}
              className="mt-6 font-heading text-[42px] sm:text-6xl lg:text-[68px] tracking-tighter leading-[1.02] font-medium">
              Your Body Leaves Signals.
              <br /><span className="gradient-text-teal-lav">HealthIRL Helps You Decode Them.</span>
            </motion.h1>

            <motion.p {...fadeUp(0.15)} className="mt-6 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed">
              HealthIRL analyzes your <span className="text-zinc-200">food, blood reports, sleep, habits, and emotional patterns</span> to predict health risks
              before disease develops.
            </motion.p>

            <motion.div {...fadeUp(0.25)} className="mt-8 flex flex-wrap items-center gap-3">
              <a href="#waitlist" data-testid="hero-join-waitlist-btn"
                 className="group inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium text-ink-900 bg-teal-soft hover:bg-teal-mid transition-all glow-teal">
                Join Beta Waitlist
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="group-hover:translate-x-0.5 transition-transform"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
              </a>
              <a href="#usp" data-testid="hero-see-demo-btn"
                 className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium text-white border border-white/15 hover:border-white/30 hover:bg-white/[0.04] transition-all">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="6 3 20 12 6 21 6 3" /></svg>
                See Product Demo
              </a>
            </motion.div>

            <motion.div {...fadeUp(0.35)} className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-[12px] text-zinc-500">
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-teal-soft" />Biomarker intelligence</div>
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-lav-500" />Mind + metabolic</div>
              <div className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-coral-500" />Preventive AI</div>
            </motion.div>
          </div>

          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1.0, ease: [0.2, 0.7, 0.2, 1], delay: 0.2 }} className="relative">
            <div className="relative glass-strong rounded-3xl p-6 sm:p-7 shadow-[0_30px_120px_-30px_rgba(20,184,166,0.4)]">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-teal-soft">Today&rsquo;s Snapshot</div>
                  <div className="font-heading text-xl tracking-tight mt-1">Mansi · Mumbai</div>
                </div>
                <div className="text-[11px] font-mono text-zinc-500">Tue · 07:42</div>
              </div>

              <div className="mt-6 grid grid-cols-4 gap-2">
                {scores.map((s) => <Dial key={s.label} {...s} />)}
              </div>

              <div className="mt-6 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider text-teal-soft">Biomarker trend · HbA1c</div>
                  <div className="text-[10px] font-mono text-zinc-500">last 90 days</div>
                </div>
                <BiomarkerTrend />
                <div className="mt-1 flex items-baseline gap-3">
                  <span className="font-heading text-2xl tracking-tighter">5.4</span>
                  <span className="text-[11px] text-teal-soft">▼ 0.3 · trending better</span>
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-lav-500/25 bg-lav-500/[0.06] p-4">
                <div className="flex items-start gap-3">
                  <div className="h-7 w-7 rounded-md bg-lav-500/25 flex items-center justify-center shrink-0">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#C4B5FD" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3 2 5 5 2-5 2-2 5-2-5-5-2 5-2z"/></svg>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-lav-400">Pattern detected</div>
                    <div className="text-sm text-zinc-200 mt-0.5">
                      Poor sleep last 3 nights → sugar cravings ↑ 42% → glucose spikes at 4pm. Try 15-min sun walk after lunch.
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <motion.div initial={{ y: 6, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.9, duration: 0.7 }}
              className="hidden sm:flex absolute -right-4 -bottom-4 glass rounded-full px-3 py-1.5 text-[11px] text-coral-400 items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full bg-coral-500 animate-pulse-glow" />
              Emotional-eating risk: elevated
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
